import { useState, type MouseEvent, type ReactNode } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useProjectsDeliveryTimelines } from '@/api/hooks'
import { ProjectTimelineBar } from '@/features/projects/project-expected-timeline'
import { formatTrackingNumbers } from '@/lib/shipments'
import { ProjectNeedByPicker } from '@/components/need-by-picker'
import { ProjectPriorityPicker } from '@/components/priority-picker'
import { ProjectServiceTierPicker } from '@/components/service-tier'
import { asServiceTier } from '@/lib/service-tier'
import { StatusPill } from '@/components/status-pill'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Calendar, ChevronDown, ExternalLink, Truck } from 'lucide-react'
import { formatDateOnly, formatMoney, formatPercent, formatStatus, parseUTCDate } from '@/lib/utils'
import { PIPELINE_CATEGORY_COLORS } from '@/lib/status'
import { ProductSummaryList } from '@/components/product-summary-list'
import { calculateProjectFinancials, toProductSummaryItem } from '@/components/project-po-utils'
import type { components } from '@/api/schema'

type Project = components['schemas']['InternalProjectCardRead']
type TrackingInfo = components['schemas']['TrackingInfoItem']

const SHOPIFY_SOURCE = { label: 'Shopify', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' }
const INTERNAL_SOURCE = { label: 'Internal', className: 'border-sky-200 bg-sky-50 text-sky-700' }
const OTHER_SOURCE = { label: 'Other', className: 'border-muted bg-muted text-muted-foreground' }

// Where a project came from: the store, our own quotes and dropship uploads, or anything else. Its
// service tier is a separate pill (USA-455).
const SOURCE_TYPE_META: Record<string, typeof SHOPIFY_SOURCE> = {
  shopify: SHOPIFY_SOURCE,
  shopify_order: SHOPIFY_SOURCE,
  dropship: INTERNAL_SOURCE,
  project_workflow: INTERNAL_SOURCE,
  project_workflow_order: INTERNAL_SOURCE,
  dropship_upload: INTERNAL_SOURCE,
  dropship_upload_v2: INTERNAL_SOURCE,
  internal: INTERNAL_SOURCE,
}

export function SourceBadge({ sourceType }: { sourceType: string | null | undefined }) {
  const meta = SOURCE_TYPE_META[sourceType?.trim().toLowerCase() ?? ''] ?? OTHER_SOURCE
  return (
    <Badge variant="outline" className={`text-xs ${meta.className}`}>
      {meta.label}
    </Badge>
  )
}

function POSummaryBadge({ summary }: { summary: string | null | undefined }) {
  if (!summary) return <span className="text-xs text-muted-foreground">No POs</span>
  const cat = PIPELINE_CATEGORY_COLORS[summary]
  if (!cat) return <Badge variant="secondary" className="text-xs">{formatStatus(summary)}</Badge>
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cat.bg} ${cat.text}`}>
      {cat.label}
    </span>
  )
}

function deliveryStatusVariant(
  status: string | null | undefined,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'delivered':
      return 'default'
    case 'in_transit':
    case 'partially_delivered':
      return 'secondary'
    case 'partially_shipped':
      return 'outline'
    default:
      return 'outline'
  }
}

function TrackingCell({ items }: { items: TrackingInfo[] }) {
  const [open, setOpen] = useState(false)
  if (!items.length) return null

  if (items.length === 1) {
    const item = items[0]!
    const label = (item.tracking && formatTrackingNumbers(item.tracking)) || item.pro || item.carrier || '-'
    if (item.shipment_uuid) {
      return (
        <Link
          to="/shipments/$shipmentId"
          params={{ shipmentId: item.shipment_uuid }}
          className="text-primary hover:underline text-xs"
        >
          {label}
        </Link>
      )
    }
    return <span className="text-xs">{label}</span>
  }

  return (
    <PickerMenu open={open} onOpenChange={setOpen}>
      <PickerMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs">
          {items.length} shipments <ChevronDown className="ml-1 h-3 w-3" />
        </Button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Shipments" titleOnPhoneOnly align="end">
        <PickerMenuList>
          {items.map((item, i) => (
            <PickerMenuItem key={i} asChild onSelect={() => setOpen(false)}>
              <Link
                to="/shipments/$shipmentId"
                params={{ shipmentId: item.shipment_uuid ?? '' }}
                className="flex items-center gap-2"
              >
                <span className="text-xs font-medium">{item.carrier || 'Unknown'}</span>
                <span className="text-xs text-muted-foreground">
                  {(item.tracking && formatTrackingNumbers(item.tracking)) || item.pro || '-'}
                </span>
                <ExternalLink className="ml-auto h-3 w-3" />
              </Link>
            </PickerMenuItem>
          ))}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}

export function ProjectsTableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <>
      {/* Desktop table skeleton */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Timeline</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead className="text-right">Financials</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                    <div className="flex gap-2">
                      <Skeleton className="h-5 w-16" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-7 w-full" />
                    <Skeleton className="h-7 w-3/4" />
                  </div>
                </TableCell>
                <TableCell>
                  <TimelineSkeleton />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-20" />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-end gap-1">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card skeleton */}
      <div className="space-y-2 md:grid md:grid-cols-2 md:gap-3 md:space-y-0 lg:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="rounded-lg border bg-card p-3 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-7 w-full" />
          </div>
        ))}
      </div>
    </>
  )
}

/** Where the project's shipments stand; the desktop table also counts bookings and says when nothing ships yet. */
function DeliveryBadge({
  project,
  earliestShip,
  desktop = false,
}: {
  project: Project
  earliestShip: string | null | undefined
  desktop?: boolean
}) {
  const status = project.delivery_status
  if (
    status === 'delivered' ||
    status === 'in_transit' ||
    status === 'partially_delivered' ||
    status === 'partially_shipped'
  ) {
    return (
      <Badge variant={deliveryStatusVariant(status)} className="text-xs">
        {formatStatus(status)}
      </Badge>
    )
  }
  const booked = project.booked_count ?? 0
  if (booked > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        <Truck className="h-3 w-3" />
        Booked
        {desktop && booked > 1 && ` ×${booked}`}
      </span>
    )
  }
  if (earliestShip) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
        <Calendar className="h-3 w-3" />
        Awaiting pickup
      </span>
    )
  }
  if (!desktop) return null
  return (
    <span className="text-xs text-muted-foreground">{project.po_status_summary ? 'Not shipped' : 'No POs'}</span>
  )
}

function TimelineSkeleton() {
  return (
    <div className="w-full min-w-36 space-y-1.5">
      <Skeleton className="h-2 w-full" />
      <Skeleton className="h-3 w-24" />
    </div>
  )
}

/** Revenue, then Profit and Margin once Net is known. */
function FinancialFigures({ orders }: { orders: Project['orders'] }) {
  const { revenue, profit, margin } = calculateProjectFinancials(orders ?? [])
  return (
    <>
      <p className="text-sm font-semibold">{formatMoney(revenue)}</p>
      <p className="text-[11px] text-muted-foreground">
        {profit == null ? 'Net unknown' : `${formatMoney(profit)} · ${formatPercent(margin)}`}
      </p>
    </>
  )
}

// A click on any of these inside a row is theirs, not the row's (USA-1498).
const OWN_CLICK = 'a, button, input, select, textarea, label, [role="button"], [role="menuitem"], [role="option"], [role="checkbox"], [role="combobox"]'

export function ProjectsTable<T extends Project>({
  items,
  showCustomer = true,
  payment,
  detail,
}: {
  items: T[]
  /** Off on a customer's own page, where every row is theirs. */
  showCustomer?: boolean
  /** The project's payment state, under its financials (customer page). */
  payment?: (project: T) => ReactNode
  /** A page's own line under each project's status, e.g. why sales would call (USA-1330). */
  detail?: (project: T) => ReactNode
}) {
  const navigate = useNavigate()
  const timelines = useProjectsDeliveryTimelines(items.map((project) => project.uuid))

  // The whole row opens the project; links, pickers and their menus keep their own clicks.
  const openRow = (event: MouseEvent<HTMLTableRowElement>, projectUuid: string) => {
    const target = event.target as Element
    // Menus render in a portal: their clicks bubble here through React but are not in the row.
    if (!event.currentTarget.contains(target) || target.closest(OWN_CLICK)) return
    if (window.getSelection()?.toString()) return
    if (event.metaKey || event.ctrlKey) {
      window.open(`/projects/${projectUuid}`, '_blank', 'noopener')
      return
    }
    void navigate({ to: '/projects/$projectId', params: { projectId: projectUuid } })
  }

  const timelineCell = (project: T) =>
    timelines.isLoading ? (
      <TimelineSkeleton />
    ) : (
      <ProjectTimelineBar timelines={timelines.data?.get(project.uuid) ?? []} />
    )

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Timeline</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead className="text-right">Financials</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((project) => {
              const orders = project.orders ?? []
              const trackingItems = (project.tracking_info ?? []) as TrackingInfo[]
              const allLineItems = orders.flatMap((o) => o.line_items ?? [])
              const openActions = project.open_action_count ?? 0
              const earliestShip =
                project.earliest_pickup_date ?? project.earliest_estimated_ship_date

              return (
                <TableRow
                  key={project.id}
                  className="cursor-pointer"
                  onClick={(event) => openRow(event, project.uuid)}
                >
                  <TableCell>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Link
                          to="/projects/$projectId"
                          params={{ projectId: project.uuid }}
                          className="font-medium text-primary hover:underline"
                        >
                          {project.name}
                        </Link>
                        {openActions > 0 && (
                          <span
                            title={`${openActions} open action${openActions > 1 ? 's' : ''}`}
                            className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white"
                          >
                            {openActions}
                          </span>
                        )}
                      </div>
                      <div className="flex min-w-0 items-center gap-1.5 text-xs">
                        <ProjectPriorityPicker projectUuid={project.uuid} priority={project.priority} />
                        {showCustomer && project.customer_group ? (
                          <Link
                            to="/customers/$groupId"
                            params={{ groupId: project.customer_group.uuid }}
                            className="truncate text-muted-foreground hover:text-primary hover:underline"
                          >
                            {project.customer_group.name}
                          </Link>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <SourceBadge sourceType={project.source_type} />
                        {project.status_summary ? (
                          <StatusPill tone={project.status_summary.headline.tone}>
                            {project.status_summary.headline.label}
                          </StatusPill>
                        ) : (
                          <POSummaryBadge summary={project.po_status_summary} />
                        )}
                        <span className="text-xs text-muted-foreground">
                          {project.date_placed
                            ? parseUTCDate(project.date_placed).toLocaleDateString()
                            : ''}
                        </span>
                      </div>
                      {detail ? <div className="pt-1 whitespace-normal">{detail(project)}</div> : null}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-0 max-w-[320px] whitespace-normal">
                    <ProductSummaryList
                      items={allLineItems.map(toProductSummaryItem)}
                      linkEntities={false}
                    />
                  </TableCell>
                  <TableCell className="w-[220px] min-w-[180px] whitespace-normal">
                    {timelineCell(project)}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <ProjectServiceTierPicker projectUuid={project.uuid} tier={asServiceTier(project.service_tier)} hideStandard />
                        <ProjectNeedByPicker
                          projectUuid={project.uuid}
                          needBy={project.need_by}
                          className="text-xs"
                        />
                      </div>
                      <DeliveryBadge project={project} earliestShip={earliestShip} desktop />
                      {earliestShip && project.delivery_status !== 'delivered' && (
                        <p className="text-xs font-semibold text-green-700">
                          {project.earliest_pickup_date ? 'Pickup' : 'Est. ship'}{' '}
                          {formatDateOnly(earliestShip)}
                        </p>
                      )}
                      {trackingItems.length > 0 && (
                        <div>
                          <TrackingCell items={trackingItems} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <FinancialFigures orders={orders} />
                    {payment ? <div className="mt-1">{payment(project)}</div> : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card list */}
      <div className="space-y-2 md:grid md:grid-cols-2 md:gap-3 md:space-y-0 lg:hidden">
        {items.map((project) => {
          const orders = project.orders ?? []
          const allLineItems = orders.flatMap((o) => o.line_items ?? [])
          const openActions = project.open_action_count ?? 0
          const earliestShip =
            project.earliest_pickup_date ?? project.earliest_estimated_ship_date

          return (
            <Link
              key={project.id}
              to="/projects/$projectId"
              params={{ projectId: project.uuid }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-primary">{project.name}</p>
                    {openActions > 0 && (
                      <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
                        {openActions}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
                    <ProjectPriorityPicker projectUuid={project.uuid} priority={project.priority} />
                    {showCustomer && project.customer_group && (
                      <p className="truncate text-xs text-muted-foreground">
                        {project.customer_group.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <FinancialFigures orders={orders} />
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <ProjectServiceTierPicker projectUuid={project.uuid} tier={asServiceTier(project.service_tier)} hideStandard />
                <ProjectNeedByPicker projectUuid={project.uuid} needBy={project.need_by} className="text-xs" />
                <SourceBadge sourceType={project.source_type} />
                {project.status_summary ? (
                  <StatusPill tone={project.status_summary.headline.tone}>
                    {project.status_summary.headline.label}
                  </StatusPill>
                ) : (
                  <POSummaryBadge summary={project.po_status_summary} />
                )}
                {payment?.(project)}
                <DeliveryBadge project={project} earliestShip={earliestShip} />
                {earliestShip && project.delivery_status !== 'delivered' && (
                  <span className="text-[11px] font-semibold text-green-700">
                    {project.earliest_pickup_date ? 'Pickup' : 'Ship'}{' '}
                    {formatDateOnly(earliestShip)}
                  </span>
                )}
              </div>

              {detail ? <div className="mt-2">{detail(project)}</div> : null}

              <div className="mt-2">{timelineCell(project)}</div>

              {allLineItems.length > 0 && (
                <div className="mt-2 space-y-0.5 border-t pt-2">
                  <ProductSummaryList
                    items={allLineItems.map(toProductSummaryItem)}
                    linkEntities={false}
                  />
                </div>
              )}
            </Link>
          )
        })}
      </div>
    </>
  )
}
